<template>
  <div>
    <v-card-title class="mb-2">QR Code Markup Preview</v-card-title>
    <v-card class="pa-8">
      <v-card-text
        class="text-center"
        :style="`color:${typographyStyles.color}; font-family:${typographyStyles.fontFamily}; line-height:${typographyStyles.lineHeight}; font-weight:${typographyStyles.fontWeight}; font-size:${typographyStyles.fontSize}`"
      >
        <!-- Above image area: prefer custom image when toggled, otherwise logo -->
        <template
          v-if="
            productSettings.useCustomImageAbove &&
            (productSettings.imageUrl || isBlobLike(abovePreviewImg))
          "
        >
          <div
            :style="{
              display: 'flex',
              justifyContent:
                productSettings.aboveImageSettings.align === 'left'
                  ? 'flex-start'
                  : productSettings.aboveImageSettings.align === 'right'
                    ? 'flex-end'
                    : 'center',
            }"
          >
            <img
              :src="productSettings.imageUrl || aboveImageUrl"
              :width="productSettings.aboveImageSettings.width || 200"
              :height="productSettings.aboveImageSettings.height || 'auto'"
              :style="{ maxWidth: '100%' }"
              alt="Custom Image"
            />
          </div>
        </template>
        <template v-else>
          <!-- Brand Image -->
          <div
            v-if="productSettings.showBrandImage"
            :style="{
              display: 'flex',
              justifyContent:
                productSettings.aboveImageSettings.align === 'left'
                  ? 'flex-start'
                  : productSettings.aboveImageSettings.align === 'right'
                    ? 'flex-end'
                    : 'center',
            }"
          >
            <img
              :src="productSettings.brandImage"
              :width="productSettings.aboveImageSettings.width || 200"
              :height="productSettings.aboveImageSettings.height || 'auto'"
              :style="{ maxWidth: '100%' }"
              alt="Company Logo"
            />
          </div>
        </template>
        <!-- Above QR Code Text -->
        <p
          v-if="productSettings.aboveTextSettings.text.length > 0"
          :class="{
            'text-left': productSettings.aboveTextSettings.align === 'left',
            'text-center': productSettings.aboveTextSettings.align === 'center',
            'text-right': productSettings.aboveTextSettings.align === 'right',
          }"
          :style="`line-height:${productSettings.typographySettings.markupLineHeight}${productSettings.typographySettings.markupLineHeightUnit}`"
        >
          {{ productSettings.aboveTextSettings.text }}
        </p>
        <!-- QR Code -->
        <img
          :src="qrcode"
          alt="QR Code"
          :style="{ width: '350px', maxWidth: '100%' }"
          class="mx-auto"
        />
        <!-- Below Image -->
        <div>
          <div v-if="belowPreviewImg">
            <v-img
              :src="belowImageUrl"
              :width="productSettings.belowImageSettings.width + 'px'"
              :height="
                productSettings.belowImageSettings.height === 'auto'
                  ? 'auto'
                  : productSettings.belowImageSettings.height + 'px'
              "
              :class="{
                'ml-auto': productSettings.belowImageSettings.align === 'right',
                'mr-auto': productSettings.belowImageSettings.align === 'left',
                'mx-auto':
                  productSettings.belowImageSettings.align === 'center',
              }"
            ></v-img>
          </div>
          <!-- Below QR Code Text -->
          <div v-if="productSettings.belowTextSettings.text.length > 0">
            <p
              :class="{
                'text-left': productSettings.belowTextSettings.align === 'left',
                'text-center':
                  productSettings.belowTextSettings.align === 'center',
                'text-right':
                  productSettings.belowTextSettings.align === 'right',
              }"
            >
              {{ productSettings.belowTextSettings.text }}
            </p>
          </div>
        </div>
        <!-- vehicle title -->
        <div
          v-if="productSettings.vehicleTitleSettings.selectedVehicleTitles"
          :class="{
            'text-left': productSettings.vehicleTitleSettings.align === 'left',
            'text-center':
              productSettings.vehicleTitleSettings.align === 'center',
            'text-right':
              productSettings.vehicleTitleSettings.align === 'right',
          }"
        >
          {{
            productSettings.vehicleTitleSettings.selectedVehicleTitles.join(' ')
          }}
        </div>
        <div
          v-if="productSettings.vehicleTitleSettings.displayCarVin"
          :class="{
            'text-left': productSettings.vehicleTitleSettings.align === 'left',
            'text-center':
              productSettings.vehicleTitleSettings.align === 'center',
            'text-right':
              productSettings.vehicleTitleSettings.align === 'right',
          }"
        >
          VIN: 1ABC234567DEFG891
        </div>
        <div
          v-if="productSettings.vehicleTitleSettings.displayCarStockNumber"
          :class="{
            'text-left': productSettings.vehicleTitleSettings.align === 'left',
            'text-center':
              productSettings.vehicleTitleSettings.align === 'center',
            'text-right':
              productSettings.vehicleTitleSettings.align === 'right',
          }"
        >
          Stock#: A10
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { useQRCode } from '@vueuse/integrations/useQRCode'
import { watch, ref, computed } from 'vue'
import { useAccountSettingsStore } from '@/store/accountSettings'
import { useProductSettingsStore } from '@/store/productSettings'

const props = defineProps<{
  abovePreviewImg?: File
  abovePreviewText?: string
  belowPreviewImg?: File
  belowPreviewText?: string
  vehicleTitleDetails?: string[]
}>()

const productSettings = useProductSettingsStore()
const accountSettings = useAccountSettingsStore()
const qrcode = useQRCode(accountSettings.getWebsite())

const aboveImageUrl = ref(accountSettings.getAccountLogo())
const belowImageUrl = ref('')
const imageUrl = ref('')

// Create computed properties to ensure reactivity
const typographyStyles = computed(() => ({
  color: productSettings.typographySettings.markupTextColor,
  fontFamily:
    productSettings.typographySettings.markupFontFamily || 'Roboto, sans-serif', // Use same default as TypographySettings
  lineHeight: `${productSettings.typographySettings.markupLineHeight}${productSettings.typographySettings.markupLineHeightUnit}`,
  fontWeight: productSettings.typographySettings.markupTextWeight,
  fontSize: `${productSettings.typographySettings.markupFontSize}${productSettings.typographySettings.markupFontSizeUnit}`,
}))

const isBlobLike = (val: any): val is Blob => {
  return (
    typeof val === 'object' &&
    val !== null &&
    (val instanceof Blob ||
      (typeof (val as any).size === 'number' &&
        typeof (val as any).type === 'string' &&
        typeof (val as any).arrayBuffer === 'function'))
  )
}

const previewImage = (type: string) => {
  if (type === 'above') {
    if (isBlobLike(props.abovePreviewImg)) {
      aboveImageUrl.value = URL.createObjectURL(props.abovePreviewImg)
    }
  }
  if (type === 'below') {
    if (isBlobLike(props.belowPreviewImg)) {
      belowImageUrl.value = URL.createObjectURL(props.belowPreviewImg)
    }
  }
}

// Watch for typography changes to ensure preview updates
watch(
  () => productSettings.typographySettings,
  () => {
    // Force reactivity by accessing the computed property
    typographyStyles.value
  },
  { deep: true },
)

// Specifically watch for font family changes
watch(
  () => productSettings.typographySettings.markupFontFamily,
  newFontFamily => {
    console.log('Font family changed to:', newFontFamily)
    // Force reactivity update
    typographyStyles.value
  },
)

watch(
  () => props.abovePreviewImg,
  () => {
    previewImage('above')
  },
  { immediate: true },
)
watch(
  () => props.belowPreviewImg,
  () => {
    previewImage('below')
  },
  { immediate: true },
)
</script>
